/**
 * "Voltar para onde eu estava": quem cai no login por sessão vencida volta ao caminho que abriu,
 * em vez de sempre à home.
 *
 * O destino chega por query string, então é entrada não confiável: só vale caminho interno do
 * portal sob `/admin/`. URL externa (`//evil.com`, `https://…`), `javascript:` e barra invertida
 * (que alguns navegadores tratam como `/`) caem no destino padrão — senão o login viraria um
 * redirecionador aberto para phishing.
 */
export const PARAM_VOLTAR = "voltar";
const CHAVE_VOLTAR = "voltarAposLogin";
/** Barra invertida e caracteres de controle (quebra de linha, NUL…) nunca fazem parte de um caminho válido. */
const temCaractereSuspeito = (v: string) =>
  Array.from(v).some((c) => c === "\\" || c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127);

/**
 * Segmento `.`/`..` (inclusive codificado como `%2e`) e barra dupla: o navegador normaliza
 * `/admin/../auth` para `/auth`, e o destino deixaria de estar sob `/admin/`.
 */
const temSegmentoQueSai = (v: string) => {
  const caminho = v.split(/[?#]/)[0];
  return caminho.includes("//") || caminho.split("/").some((s) => /^(\.|%2e){1,2}$/i.test(s));
};

export function sanearVoltar(valor: string | null | undefined): string | null {
  if (!valor || !valor.startsWith("/admin/")) return null;
  if (temCaractereSuspeito(valor) || temSegmentoQueSai(valor)) return null;
  return valor;
}

/** `/auth/sign-in?voltar=…` para o caminho atual, ou o login puro se o caminho não for do admin. */
export function urlDoLoginComVoltar(pathname: string, search: string): string {
  const alvo = sanearVoltar(`${pathname}${search}`);
  return alvo ? `/auth/sign-in?${PARAM_VOLTAR}=${encodeURIComponent(alvo)}` : "/auth/sign-in";
}

/** Lê e sanea o `voltar` de uma query string. */
export function voltarDaBusca(search: string): string | null {
  return sanearVoltar(new URLSearchParams(search).get(PARAM_VOLTAR));
}

/**
 * O login da Microsoft sai do portal e volta em `/auth/sign-in?code=…`, sem o `voltar`. Guarda-se
 * em sessionStorage (some com a aba) antes do redirect.
 */
export function guardarVoltar(voltar: string | null): void {
  try {
    if (voltar) sessionStorage.setItem(CHAVE_VOLTAR, voltar);
    else sessionStorage.removeItem(CHAVE_VOLTAR);
  } catch {
    // Sem storage (modo restrito): volta à home, que é o comportamento anterior.
  }
}

/** Lê, apaga e sanea: vale para UMA volta. */
export function consumirVoltar(): string | null {
  try {
    const v = sessionStorage.getItem(CHAVE_VOLTAR);
    sessionStorage.removeItem(CHAVE_VOLTAR);
    return sanearVoltar(v);
  } catch {
    return null;
  }
}
